import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import InputMask from 'react-input-mask';
import { Card } from '../../assets/icons';
import { HighlightableInput } from '../../components/input';
import Button from '../../components/button';
import { BasicKeypad } from '../../components/keypad/popup-keypad';
import { PlayerCardOperator } from '../../types';
import playerCardOperators from '../providers';
import { useTheme } from '../../themes';
import { Label } from '../../components/typography';
import { closeModal } from '../../components/modal';
import { useSearchCard } from './use-card-query';
import { showFailureModal } from '../../components/modal';
import CardQueryErrorComponent from './card-query-error-component';
import { useOnManualEntry } from './use-on-manual-entry';
import { useActiveCardOnDevice } from './activate-card';
import { onCardServiceUnavailable } from '../player-card-warnings';
import { getRootCause } from '../../apollo/errors';
import { CARD_ERROR_CODES } from '../card-error-codes';

export function CardNumberEntry({ playerCardOperator }: { playerCardOperator: PlayerCardOperator }) {
  const inputRef = useRef<HTMLInputElement>();
  const [cardNumber, setCardNumber] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const numberFormat = playerCardOperators[playerCardOperator].cardNumberFormat;

  const searchCard = useSearchCard();
  const onManualEntry = useOnManualEntry();
  const theme = useTheme();
  const activeCardOnDevice = useActiveCardOnDevice();

  const searchDisabled = loading || cardNumber.length < numberFormat.min || cardNumber.length > numberFormat.max;

  useEffect(() => {
    if (activeCardOnDevice) {
      closeModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCardOnDevice]);

  return (
    <Container>
      <InputAndLabel>
        <Label textAlign="start">Enter Rewards Card Number</Label>
        <InputContainer>
          <InputMask mask={numberFormat.mask} maskChar="" value={cardNumber}>
            {() => (
              <HighlightableInput
                ref={inputRef}
                value={cardNumber}
                componentsize="standard"
                labeltext={<Card fill={theme.colors.brand.foreground} />}
                labelPadding="1rem 1.1rem .7rem 1rem"
                placeholder="Enter card number"
                width="27.5rem"
              />
            )}
          </InputMask>
          {!loading && (
            <KeypadWrapper>
              <BasicKeypad
                existingValue={cardNumber}
                maxValue={Number('9'.repeat(numberFormat.max))}
                onKeyPress={handleKeyPress}
              />
            </KeypadWrapper>
          )}
        </InputContainer>
      </InputAndLabel>

      <Actions>
        <Button width="12.6rem" modifiers="secondaryRound" onClick={closeModal}>
          Cancel
        </Button>
        <Button width="12.6rem" modifiers="primaryRound" onClick={handleSearch} disabled={searchDisabled}>
          Search
        </Button>
      </Actions>
    </Container>
  );

  function handleKeyPress(newValue: string) {
    setCardNumber(newValue);
  }

  async function handleSearch() {
    setLoading(true);
    try {
      const { data, errors } = await searchCard({ cardNumber, cardOperator: playerCardOperator });

      if (errors) {
        const errorCode = getRootCause({ graphQLErrors: errors as any })?.code;
        if (errorCode === CARD_ERROR_CODES.SERVICE_UNAVAILABLE || errorCode === CARD_ERROR_CODES.HTTP_503) {
          onCardServiceUnavailable();
        } else {
          showFailureModal({
            actions: [],
            content: <CardQueryErrorComponent close={closeModal} />,
            title: 'Add Rewards Card'
          });
        }
        return;
      }

      onManualEntry({ card: data?.card, cardOperator: playerCardOperator });
    } finally {
      setLoading(false);
    }
  }
}

const Container = styled.div`
  height: 20rem;
  display: flex;
  gap: 2.5rem;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const InputContainer = styled.div`
  position: relative;
`;
const InputAndLabel = styled.div`
  display: flex;
  gap: 0.6rem;
  flex-direction: column;
`;

const KeypadWrapper = styled.div<{ hidden?: boolean }>`
  position: absolute;
  top: -2.5rem;
  left: 29.5rem;

  display: flex;
  align-items: center;
  padding: 1.2rem;
  width: 27.6rem;
  visibility: ${({ hidden }) => (hidden ? 'hidden' : 'visible')};

  background-color: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  box-shadow: ${({ theme }) => theme.shadows.drop};
`;

const Actions = styled.div`
  display: flex;
  gap: 1.5rem;
  height: 4.4rem;
`;
